"use client";

import { useEffect, useRef, useState } from "react";
import { Transition } from "@headlessui/react";
import { InboxIcon, InboxStackIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  useDownloadExportDocs,
  useDownloadExportFile,
  usePollExportStatus,
} from "../../custom-hooks/queries";
import { formatVqqName } from "../../utils/vqqNameFormat";

export default function DownloadNotifications({
  show,
  setShow,
  fileType,
  name,
  id,
  download,
  downloadFileName,
  exportLogsId,
}) {
  const effectiveName = downloadFileName || name;
  const {
    mutate: downloadRfi,
    data: exportData,
    isError: exportExcelError,
  } = useDownloadExportFile();

  const {
    mutate: downloadRfiDocs,
    data: exportDocsData,
    isError: exportDocsError,
  } = useDownloadExportDocs();

  const { data: exportStatusData } = usePollExportStatus(exportLogsId, {
    enabled: !!exportLogsId && show,
  });

  const isExportReady = exportStatusData?.status === "Completed";
  const isExportError = exportStatusData?.status === "Error";

  const handleDownload = () => {
    const extension = fileType.includes("excel") ? ".xlsx" : ".pdf";

    if (fileType.includes("rfifiles")) {
      const rfiPayload = {
        folder: id,
        questionnaire_name: (effectiveName || "export") + extension,
      };
      downloadRfi(rfiPayload, {
        onError: (err) => {
          console.error("[DownloadNotifications] download failed:", {
            status: err?.response?.status,
            message: err?.message,
            params: rfiPayload,
          });
        },
      });
    } else if (fileType.includes("rfidocs")) {
      const docsPayload = {
        folder: id,
        questionnaire_name: (effectiveName || "export") + ".zip",
      };
      downloadRfiDocs(docsPayload, {
        onError: (err) => {
          console.error("[DownloadNotifications] download failed:", {
            status: err?.response?.status,
            message: err?.message,
            params: docsPayload,
          });
        },
      });
    } else if (fileType.includes("assessmentDetails")) {
      download();
    }
  };

  const lastZipDownloadedRef = useRef(null);

  useEffect(() => {
    const downloadLink = exportDocsData?.download_link;

    if (!downloadLink || exportDocsError) return;
    if (lastZipDownloadedRef.current === downloadLink) return;
    lastZipDownloadedRef.current = downloadLink;

    fetch(downloadLink)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");

        const contentDisposition = res.headers.get("Content-Disposition");
        let fileName = "document";

        if (contentDisposition?.includes("filename=")) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match?.[1]) {
            fileName = match[1].split(".")[0];
          }
        }

        return res.blob().then((blob) => ({ blob, fileName }));
      })
      .then(({ blob, fileName }) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        // effectiveName may be the raw stored VQQ name (branding prefix + RFI
        // token); normalize for the saved filename only. The API payload above
        // must keep the raw name for the blob lookup.
        link.download = `${formatVqqName(effectiveName) || fileName}.zip`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("ZIP download failed:", error);
      });
  }, [exportDocsData?.download_link, exportDocsError]);

  const lastExportDownloadedRef = useRef(null);

  useEffect(() => {
    const downloadLink = exportData?.download_link;

    if (!downloadLink || exportExcelError) return;
    if (lastExportDownloadedRef.current === downloadLink) return;
    lastExportDownloadedRef.current = downloadLink;

    fetch(downloadLink)
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");

        const contentDisposition = res.headers.get("Content-Disposition");
        const contentType = res.headers.get("Content-Type");

        let fileName = "document";
        let fileExtension = "xlsx"; // default

        if (contentDisposition?.includes("filename=")) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match?.[1]) {
            const parts = match[1].split(".");
            fileName = parts[0];
            fileExtension = parts[1] || "xlsx";
          }
        } else if (downloadLink.includes("?")) {
          const match = downloadLink.match(/\.([a-zA-Z0-9]+)(\?|$)/);
          if (match?.[1]) {
            fileExtension = match[1];
          }
        }

        return res.blob().then((blob) => ({ blob, fileName, fileExtension }));
      })
      .then(({ blob, fileName, fileExtension }) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${formatVqqName(effectiveName) || fileName}.${fileExtension}`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error("File download failed:", error);
      });
  }, [exportData?.download_link, exportExcelError]);

  return (
    <>
      {/* Global notification live region, render this permanently at the end of the document */}
      <div
        aria-live="assertive"
        className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6 z-50"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
          <Transition show={show}>
            <div className="pointer-events-auto w-full max-w-sm rounded-lg bg-white shadow-lg outline outline-1 outline-black/5 transition data-[closed]:data-[enter]:translate-y-2 data-[enter]:transform data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-100 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:data-[enter]:sm:translate-x-2 data-[closed]:data-[enter]:sm:translate-y-0">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="shrink-0">
                    <InboxStackIcon
                      aria-hidden="true"
                      className="size-6 text-gray-400"
                    />
                  </div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p className="text-sm font-medium text-gray-900">
                      {exportLogsId && !isExportReady && !isExportError
                        ? "Preparing Export..."
                        : isExportError
                        ? "Export Failed"
                        : "Document Export Ready"}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      {exportLogsId && !isExportReady && !isExportError
                        ? `Generating ${formatVqqName(effectiveName)}...`
                        : isExportError
                        ? "The export encountered an error. Please try again."
                        : `Your document export ${formatVqqName(effectiveName)} is ready for download.`}
                    </p>
                    <div className="mt-3 flex space-x-7">
                      <button
                        type="button"
                        disabled={exportLogsId && !isExportReady}
                        className={`rounded-md text-sm font-medium focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-indigo-500 ${
                          exportLogsId && !isExportReady
                            ? "text-gray-400 cursor-not-allowed"
                            : "text-indigo-600 hover:text-indigo-500"
                        }`}
                        onClick={() => {
                          handleDownload();
                          setShow(false);
                        }}
                      >
                        {exportLogsId && !isExportReady && !isExportError
                          ? "Processing..."
                          : "Download"}
                      </button>
                    </div>
                  </div>
                  <div className="ml-4 flex shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setShow(false);
                      }}
                      className="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-indigo-600"
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon aria-hidden="true" className="size-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  );
}
